import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NgFor } from '@angular/common';
import { IconComponent } from '@app/components/icon/icon.component';
import { GenericFilterConfig } from '@app/models/filters/generic-filter-config';
import { DatepickerFilterConfig } from '@app/models/filters/datepicker-filter-config';
import { FilterStatus } from '@app/models/filters/filter-status';
import { InfoButtonComponent } from '@app/components/info-modal/info-button/info-button.component';
import { ModalComponent } from '@app/components/info-modal/modal/modal.component';
import { ModalItem } from '@app/models/elements/modal-item';

@Component({
	standalone: true,
	imports: [NgFor, IconComponent, InfoButtonComponent, ModalComponent],
	selector: 'app-multi-datepicker',
	templateUrl: './multi-datepicker.component.html',
	styleUrls: ['./multi-datepicker.component.css'],
})
export class MultiDatepickerComponent implements OnInit {
	@Input() genericConfig: GenericFilterConfig = {} as GenericFilterConfig;
	specificConfig: DatepickerFilterConfig = {} as DatepickerFilterConfig;

	@Output() dateSelected = new EventEmitter<FilterStatus>();

	dateRangeStart: Date = {} as Date;
	dateRangeEnd: Date = {} as Date;

	currentDate: Date = {} as Date;
	visibleMonthStart: Date = {} as Date;
	visibleMonthEnd: Date = {} as Date;
	disableBackButton = false;
	disableNextButton = true;

	selectedDates: Date[] = [];
	selectedDatesModal: ModalItem = {
		header: 'Valori selezionati',
		body: 'Nessuno',
	};

	months: string[] = [
		'Gennaio',
		'Febbraio',
		'Marzo',
		'Aprile',
		'Maggio',
		'Giugno',
		'Luglio',
		'Agosto',
		'Settembre',
		'Ottobre',
		'Novembre',
		'Dicembre',
	];

	constructor() {}

	ngOnInit(): void {
		this.specificConfig = this.genericConfig as DatepickerFilterConfig;

		// cast date string to date object
		let dateStartTime: number = Date.parse(this.specificConfig.dateRangeStart);
		this.dateRangeStart = new Date(dateStartTime);
		let dateEndTime: number = Date.parse(this.specificConfig.dateRangeEnd);
		this.dateRangeEnd = new Date(dateEndTime);

		this.currentDate = new Date(
			this.dateRangeEnd.getFullYear(),
			this.dateRangeEnd.getMonth(),
			this.dateRangeEnd.getDate(),
		);

		this.visibleMonthStart = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), 1);
		this.visibleMonthEnd = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 0);
		this.selectDate(this.visibleMonthEnd);
	}

	ngAfterViewInit() {
		// last available date is initially selected
		this.selectedDatesModal = {
			...this.selectedDatesModal,
			body: this.getSelectedDatesString(this.selectedDates),
		};
	}

	selectDate(date: Date): void {
		const index = this.selectedDates.findIndex((d) => d.getTime() === date.getTime());
		if (index === -1) {
			if (this.selectedDates.length < this.specificConfig.selectionLimit) {
				this.selectedDates.push(date);
				this.selectedDatesModal = {
					...this.selectedDatesModal,
					body: this.getSelectedDatesString(this.selectedDates),
				};

				let status = this.buildStatus(this.selectedDates);
				this.dateSelected.emit(status);
			} else {
				alert(`Non è consentito selezionare più di ${this.specificConfig.selectionLimit} giorni.`);
			}
		} else {
			this.selectedDates.splice(index, 1);
			this.selectedDatesModal = {
				...this.selectedDatesModal,
				body: this.getSelectedDatesString(this.selectedDates),
			};
			let status = this.buildStatus(this.selectedDates);
			this.dateSelected.emit(status);
		}
	}

	isDateSelected(date: Date): boolean {
		return this.selectedDates.some((d) => d.getTime() === date.getTime());
	}

	toggleMonth(offset: number): void {
		this.currentDate.setMonth(this.currentDate.getMonth() + offset);

		let testDate = new Date(
			this.currentDate.getFullYear(),
			this.currentDate.getMonth(),
			this.currentDate.getDate(),
		);
		testDate.setMonth(testDate.getMonth() + offset);

		if (testDate > this.dateRangeEnd) {
			this.disableNextButton = true;
		} else {
			this.disableNextButton = false;
		}

		if (testDate < this.dateRangeStart) {
			this.disableBackButton = true;
		} else {
			this.disableBackButton = false;
		}

		const year = this.currentDate.getFullYear();
		const month = this.currentDate.getMonth();
		this.visibleMonthStart = new Date(year, month, 1);
		this.visibleMonthEnd = new Date(year, month + 1, 0);
	}

	daysInMonth(year: number, month: number): number {
		return new Date(year, month + 1, 0).getDate();
	}

	getDaysArray(year: number, month: number): number[] {
		let firstOfMonth = new Date(year, month, 1);
		let dayOfWeek = firstOfMonth.getDay() - 1;

		// add 0s to the start of the days array to start the calendar with empty spaces
		// if the current month doesn't start on monday
		let days = [];
		for (let i = 0; i < dayOfWeek; i++) {
			days.push(0);
		}

		// add rest of the days
		days.push(...Array.from({ length: this.daysInMonth(year, month) }, (_, i) => i + 1));

		return days;
	}

	createDate(year: number, month: number, day: number): Date {
		return new Date(year, month, day);
	}

	private getSelectedDatesString(selectedDates: Date[]) {
		let content = [];
		if (selectedDates.length > 0) {
			for (let date of selectedDates) {
				content.push(date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }));
			}
			return content.join(', ');
		} else {
			return 'Nessuno';
		}
	}

	private buildStatus = (selectedDates: Date[]): FilterStatus => {
		let selectedPrimaryValues = [];
		for (let date of selectedDates) {
			let toString =
				String(date.getFullYear()) +
				String(date.getMonth() + 1).padStart(2, '0') +
				String(date.getDate()).padStart(2, '0');
			selectedPrimaryValues.push(toString);
		}

		let result: FilterStatus = {
			filterId: this.specificConfig.id,
			selectedPrimaryValues: selectedPrimaryValues,
			selectedSecondaryValues: selectedPrimaryValues,
		};
		return result;
	};
}
